<template>
    <div>
        <b-form-group label="Composant depuis un fichier" label-size="sm" class="mb-2">
            <b-form-select
                v-model="selectedComponentFile"
                :options="componentFileOptions"
                size="sm"
                @change="onComponentFileChange"
            />
        </b-form-group>

        <div v-if="selectedComponentFile" class="alert alert-info py-2">
            <div><strong>Composant sélectionné :</strong> {{ selectedComponentFile }}</div>
            <div v-if="selectedComponentInfo"><strong>Info :</strong> {{ selectedComponentInfo }}</div>
            <div v-else class="text-muted">Aucun commentaire d'information trouvé dans ce composant.</div>
        </div>

        <p v-if="selectedComponentFile" class="text-muted">
            <small>Le composant sera chargé directement depuis le fichier sélectionné. L'éditeur de code est masqué.</small>
        </p>

        <p v-else>
            <span v-if="!testMode">Code du composant Vue.js</span>
            <CodeGenerator
                v-model="component_code"
                :inline="testMode"
                :build-request="buildComponentRequest"
                :validate-code="validateVueComponent"
                code-language="vue"
                modal-title="Génération du composant Vue.js"
                prompt-placeholder="Ex: Un composant qui affiche les derniers clients dans un tableau"
                ref="codeGenerator"
                @enter-test-mode="enterTestMode">
            </CodeGenerator>
        </p>

        <div v-if="testMode" class="codegen-test-mode-bar">
            <p class="text-muted small mb-2">
                <i class="fas fa-flask"></i> Mode test : navigation et enregistrement verrouillés. Prévisualisation disponible. Validez ou quittez.
            </p>
            <div class="d-flex justify-content-between">
                <b-button variant="outline-secondary" size="sm" @click="exitTestMode(false)">
                    <i class="fas fa-arrow-left"></i> Quitter sans valider
                </b-button>
                <b-button variant="success" size="sm" @click="exitTestMode(true)">
                    <i class="fas fa-check"></i> Valider et quitter le mode test
                </b-button>
            </div>
        </div>

        <p v-if="!selectedComponentFile" class="text-muted">
            <small>
                Écrivez un composant Vue.js complet avec template, script et style.<br>
                Le composant aura accès aux props : <code>format</code> (small/big) et <code>data</code> (paramètres du dashboard)
            </small>
        </p>

        <div v-show="!selectedComponentFile && !testMode" style="width: 100%; height: 500px; display: inline-block; position: relative;">
            <pre style="width: 100%; height: 100%" :id="'ace_editor_component-' + id"></pre>
        </div>

        <hr v-if="!selectedComponentFile">

        <b-button v-if="!selectedComponentFile" v-b-toggle.collapse-example variant="link" size="sm">
            <i class="fas fa-chevron-down"></i> Voir un exemple de composant
        </b-button>

        <b-collapse v-if="!selectedComponentFile" id="collapse-example">
            <p class="text-muted">
                <small v-pre>
                    <strong>Exemple de composant avec accès à la base de données :</strong><br>
                    <code>&lt;template&gt;<br>
                    &nbsp;&nbsp;&lt;div&gt;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;h3&gt;Mes données&lt;/h3&gt;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;p v-if="loading"&gt;Chargement...&lt;/p&gt;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;p v-else&gt;{{ count }} résultats&lt;/p&gt;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;edit_tabulator ref="tabulator" :extern_file="false" /&gt;<br>
                    &nbsp;&nbsp;&lt;/div&gt;<br>
                    &lt;/template&gt;<br>
                    <br>
                    &lt;script&gt;<br>
                    export default {<br>
                    &nbsp;&nbsp;props: ['format', 'data'],<br>
                    &nbsp;&nbsp;data() {<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;return {<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;loading: true,<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;count: 0,<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;results: []<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;}<br>
                    &nbsp;&nbsp;},<br>
                    &nbsp;&nbsp;async mounted() {<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;// Accès au service database<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;this.results = await this.database.get('select', 'ma_table', {<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;select: '*',<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;where: ''<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;end: 'LIMIT 10'<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;});<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;this.count = this.results.length;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;this.loading = false;<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;// Afficher dans le tabulator<br>
                    &nbsp;&nbsp;&nbsp;&nbsp;this.$refs.tabulator.set_data(this.results);<br>
                    &nbsp;&nbsp;}<br>
                    }<br>
                    &lt;/script&gt;<br>
                    <br>
                    &lt;style scoped&gt;<br>
                    h3 { color: #42b983; }<br>
                    &lt;/style&gt;</code>
                    <br><br>
                    <strong>Services disponibles :</strong><br>
                    • <code>this.database</code> : service d'accès à la base de données<br>
                    &nbsp;&nbsp;- get(action, table, params)<br>
                    • <code>this.code</code> : exécution de code sandboxé (accès base de données + utilisateur courant)<br>
                    &nbsp;&nbsp;- code(code, data) : exécute le code passé en argument<br>
                    &nbsp;&nbsp;- run_by_name(name, data, version) : exécute un code sauvegardé dans la bibliothèque (version optionnelle, défaut = production)<br>
                    <br>
                    <strong>Composants disponibles :</strong><br>
                    • <code>&lt;edit_tabulator&gt;</code> : tableau éditable
                </small>
            </p>
        </b-collapse>
    </div>
</template>

<script>
import uniqueId from "lodash.uniqueid";
import store from "../../store";
import CodeGenerator from "../CodeGenerator.vue";
import "../../../public/js/ace-builds/src-min-noconflict/ace.js";

const componentFilesContext = require.context("../vueComponent", false, /\.vue$/);

function normalizeFileName(contextKey) {
    return contextKey.replace(/^\.\//, "");
}

export default {
    components: {
        CodeGenerator
    },
    props: {
        params: {
            default: () => ({})
        }
    },
    data() {
        return {
            id: uniqueId(""),
            editor: null,
            component_code: "",
            selectedComponentFile: "",
            selectedComponentInfo: "",
            componentFileOptions: [{ value: "", text: "Mode code personnalisé" }],
            componentFileRegistry: {},

            // Mode "test en place" (TODO_CODEGEN_CHAT.md section 10).
            testMode: false
        };
    },
    watch: {
        component_code() {
            this.syncAceFromState();
        },
        selectedComponentFile() {
            this.updateSelectedComponentInfo();
        }
    },
    mounted() {
        this.initComponentFileOptions();
        this.initEditor();
    },
    methods: {
        initComponentFileOptions() {
            const files = componentFilesContext.keys().map(normalizeFileName).sort((a, b) => a.localeCompare(b));
            const registry = {};
            const options = [{ value: "", text: "Mode code personnalisé" }];

            files.forEach((fileName) => {
                const module = componentFilesContext("./" + fileName);
                const component = module && module.default ? module.default : module;
                const info = component?.builder_info || component?.builderInfo || component?.description || "";

                registry[fileName] = {
                    component,
                    info
                };

                options.push({ value: fileName, text: fileName });
            });

            this.componentFileRegistry = registry;
            this.componentFileOptions = options;

            if (this.selectedComponentFile && !registry[this.selectedComponentFile]) {
                this.selectedComponentFile = "";
            }

            this.updateSelectedComponentInfo();
        },
        updateSelectedComponentInfo() {
            if (!this.selectedComponentFile) {
                this.selectedComponentInfo = "";
                return;
            }
            this.selectedComponentInfo = this.componentFileRegistry?.[this.selectedComponentFile]?.info || "";
        },
        onComponentFileChange() {
            this.updateSelectedComponentInfo();
        },
        enterTestMode() {
            this.testMode = true;
            this.$emit('codegen-test-mode', true);
        },
        exitTestMode(apply) {
            var cg = this.$refs.codeGenerator;
            if (apply && cg && cg.lastGeneratedCode) {
                if (cg.validation_errors && cg.validation_errors.length > 0) {
                    this.$notify({
                        group: "info",
                        type: "error",
                        text: "Le code généré contient des erreurs de validation, corrigez-les avant de valider.",
                        duration: 4000
                    });
                    return;
                }
                cg.applyGeneratedCode();
            }
            this.testMode = false;
            this.$emit('codegen-test-mode', false);
            this.$nextTick(() => {
                this.syncAceFromState(true);
                if (this.editor)
                    this.editor.resize();
            });
        },
        validateVueComponent(code) {
            const errors = [];
            if (!code || !code.trim()) {
                errors.push("Le composant est vide.");
                return errors;
            }
            if (code.indexOf("<template>") === -1) {
                errors.push("Section <template> manquante.");
            }
            if (code.indexOf("</template>") === -1) {
                errors.push("Balise de fermeture </template> manquante.");
            }
            if (code.indexOf("export default") === -1) {
                errors.push("Le script doit contenir 'export default'.");
            }
            return errors;
        },
        buildComponentRequest(prompt, currentCode) {
            return `Tu es un assistant expert pour créer des composants Vue.js 2.

Contexte:
- Vue.js version 2.7.14
- Framework CSS: Bootstrap 4 (utilise les classes Bootstrap)
- Le composant doit avoir les sections <template>, <script>, et optionnellement <style scoped>

Services disponibles dans le composant:
- this.database : service d'accès à la base de données
  * this.database.get(action, table, params)
  * Exemple: await this.database.get('select', 'clients', {select: '*', where: "id = 1", end: 'LIMIT 10'})
- this.code : exécution de code sandboxé (le code exécuté a accès à la base de données et à l'utilisateur courant)
  * this.code.code(code, data) : exécute le code JS passé en argument avec les données data
  * this.code.run_by_name(name, data, version) : exécute un code sauvegardé dans la bibliothèque (version optionnelle, défaut = production)

Composants disponibles:
- <edit_tabulator> : tableau éditable
  * Props: :extern_file="false"
  * Méthodes: this.$refs.tabulator.set_data(data)

Contraintes:
- INTERDIT: markdown, fences de code autour du code Vue.js.
- Respecte le contrat de format de réponse donné dans les instructions système (code seul, code + explication, ou question de clarification selon le mode sélectionné).
- Le code doit être valide et fonctionnel
- Utilise async/await pour les appels asynchrones
- Gère les cas d'erreur et les données vides
- Utilise Bootstrap 4 pour le style (classes: btn, btn-primary, table, card, etc.)

Règles:
- Si du code existe déjà, applique la demande EN MODIFIANT ce code et renvoie le code COMPLET final
- Le composant doit être clair, bien structuré et commenté

CODE_ACTUEL:
<BEGIN_CODE>
${currentCode || ""}
</END_CODE>

DEMANDE UTILISATEUR:
${prompt}
`;
        },
        syncAceFromState(force) {
            if (!this.editor) return;
            const currentValue = this.editor.getValue();
            const nextValue = this.component_code || "";
            if (force || currentValue !== nextValue) {
                this.editor.setValue(nextValue, -1);
            }
        },
        initEditor() {
            this.$nextTick(() => {
                if (typeof ace === "undefined")
                    return;

                const theme = (store && store.state && store.state.style && store.state.style.theme && store.state.style.theme.code)
                    ? store.state.style.theme.code
                    : "chrome";

                const editorElement = document.getElementById('ace_editor_component-' + this.id);
                if (editorElement) {
                    this.editor = ace.edit('ace_editor_component-' + this.id);
                    this.editor.setTheme("ace/theme/" + theme);
                    this.editor.session.setMode("ace/mode/html");
                    this.editor.setOptions({
                        fontSize: "12px",
                        showPrintMargin: false,
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: false
                    });

                    if (this.params && this.params.component_code) {
                        this.editor.setValue(this.params.component_code, -1);
                    } else {
                        // Template par défaut
                        const defaultTemplate = `<template>
  <div>
    <h3>Mon composant</h3>
    <p v-if="loading">Chargement...</p>
    <p v-else>{{ count }} résultats</p>
    <edit_tabulator ref="tabulator" :extern_file="false"></edit_tabulator>
  </div>
</template>

<script>
export default {
  props: ['format', 'data'],
  data() {
    return {
      loading: true,
      count: 0,
      results: []
    }
  },
  async mounted() {
    // Exemple d'accès à la base de données
    // this.results = await this.database.get('select', 'ma_table', {
    //   select: '*',
    //   end: 'LIMIT 10'
    // });
    // this.count = this.results.length;
    // this.loading = false;
    // this.$refs.tabulator.set_data(this.results);
    
    // Simulation pour l'exemple
    this.loading = false;
    console.log('Composant personnalisé monté', this.format);
  }
}
<\/script>

<style scoped>
h3 {
  color: #42b983;
}
</style>`;
                        this.editor.setValue(defaultTemplate, -1);
                    }

                    this.editor.on('change', () => {
                        this.component_code = this.editor.getValue();
                    });
                    
                    this.component_code = this.editor.getValue();
                }
            });
        },
        async load(data) {
            console.log("Loading vueComponentBuilder with data:", data);
            this.selectedComponentFile = data?.component_file || "";
            this.updateSelectedComponentInfo();

            if (!this.selectedComponentFile && data && data.component_code && this.editor) {
                this.editor.setValue(data.component_code, -1);
                this.component_code = data.component_code;
            }

            // Charger l'état du générateur de code (conversations multi-tours,
            // cf CodeGenerator.vue::load() / TODO_CODEGEN_CHAT.md section 3/4.5).
            // Persistance S3 non câblée ici (pas de scenario_uid/step_uid dans ce
            // contexte) : les chats restent stockés en entier dans le JSON du bloc.
            await this.$nextTick();
            if (this.$refs.codeGenerator) {
                this.$refs.codeGenerator.load({
                    chats: data?.codegen_chats || []
                });
            }
        },
        async save() {
            const isFileMode = !!this.selectedComponentFile;
            let code = this.editor ? this.editor.getValue() : this.component_code;
            if (this.testMode && this.$refs.codeGenerator && this.$refs.codeGenerator.lastGeneratedCode)
                code = this.$refs.codeGenerator.lastGeneratedCode;

            const payload = {
                component_file: this.selectedComponentFile || "",
                component_code: isFileMode ? "" : code
            };

            if (this.$refs.codeGenerator) {
                const codegenState = this.$refs.codeGenerator.save();
                if (codegenState.chats && codegenState.chats.length > 0)
                    payload.codegen_chats = codegenState.chats;
            }

            return payload;
        }
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    }
};
</script>

<style scoped>
</style>
